import { type RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { calculateTaxFactor, calculateTransferTaxFactor } from './index';
import {
  calculateEnhancedInterestRate,
  getBridgeRateByTerm,
} from './rateIntegration';

interface BridgeRefinanceParams {
  currentPropertyValue: number;
  existingLoanBalance?: number;
  cashOutAmount?: number;
  zipCode?: number;
  hoaFees?: number;
  marketInterestRate?: number;
  creditScore?: number;
  loanTerm?: number; // In months: 6, 12, 18, or 24
}

export const calculateBridgeRefinanceClosingDisclosure = (
  {
    currentPropertyValue,
    existingLoanBalance = 0,
    cashOutAmount = 0,
    zipCode = 0,
    hoaFees = 0,
    marketInterestRate = 10.5,
    creditScore = 0,
    loanTerm = 12, // Default 12 months
  }: BridgeRefinanceParams,
  state?: RootState
) => {
  // New loan amount includes existing balance + cash out
  const loanAmount = existingLoanBalance + cashOutAmount;
  const ltv = (loanAmount / currentPropertyValue) * 100;

  const taxFactor = calculateTaxFactor(zipCode);
  const transferTaxFactor = calculateTransferTaxFactor(zipCode);

  // Get Bridge rate by term from Redux state
  const interestRate = state
    ? getBridgeRateByTerm(state, loanTerm, creditScore, ltv / 100)
    : marketInterestRate;

  // Bridge loans are interest-only
  const monthlyPayment = (loanAmount * interestRate) / 1200;
  const balloonPayment = loanAmount;

  // Constant fees for Bridge loans (higher than conventional)
  const appraisalFeeBase = 2500;
  const processingFeeBase = 1500;
  const underwritingFee = 2000;
  const applicationFeeBase = 1000;
  const creditReportFeeBase = 200;
  const titleSettlementAgentFee = 1500;
  const countyRecordingFees = 200;
  const stateRecordingFees = 150;
  const recordingTitle = 75;
  const recordingMortgage = 75;
  const recordingDeedBase = 75;
  const floodMonitoringFee = 25;
  const floodDeterminationFee = 35;
  const pestInspectionFee = 145;
  const surveyFee = 600;
  const insuranceBinder = 150;
  const lenderCredits = 0;
  const prepaidsPropertyTaxes = -100;

  // Bridge loan specific fees
  const bridgeFee = loanAmount * 0.02;
  const exitFee = loanAmount * 0.01;

  // Factor-based calculations
  const annualPropertyTax = (currentPropertyValue * taxFactor) / 100;
  const monthlyPropertyTaxes = annualPropertyTax / 12;

  // Title insurance
  const titleTitleSearchBase = 500;
  const titleLendersTitleInsuranceBase = currentPropertyValue * 0.0065;
  const optionalOwnersTitleInsuranceBase = currentPropertyValue * 0.00575;
  const minimumOwnersTitle = 1650;
  const maximumOwnersTitle = 5850;
  const optionalOwnersTitleInsurance = Math.min(
    Math.max(optionalOwnersTitleInsuranceBase, minimumOwnersTitle),
    maximumOwnersTitle
  );

  // Insurance and prepaids
  const monthlyHomeownersInsurance = (currentPropertyValue * 0.01035) / 12;
  const prepaidsHomeownersInsurance = monthlyHomeownersInsurance * 3;
  const initialEscrowTaxes = monthlyPropertyTaxes * 6;

  // Prepaid interest (15 days)
  const dailyInterestRate = interestRate / 365 / 100;
  const prepaidInterest = loanAmount * dailyInterestRate * 15;

  // Transfer taxes (refinance typically doesn't have transfer taxes, but including for calculation)
  const transferTaxes = 0; // No transfer tax on refinance

  // Calculate totals
  const totalOriginationFees =
    underwritingFee +
    applicationFeeBase +
    processingFeeBase +
    creditReportFeeBase +
    bridgeFee;

  const totalOtherFees =
    appraisalFeeBase +
    titleSettlementAgentFee +
    floodDeterminationFee +
    floodMonitoringFee +
    pestInspectionFee +
    surveyFee +
    countyRecordingFees +
    stateRecordingFees +
    prepaidInterest +
    prepaidsHomeownersInsurance +
    initialEscrowTaxes +
    optionalOwnersTitleInsurance +
    insuranceBinder +
    titleTitleSearchBase +
    titleLendersTitleInsuranceBase +
    recordingTitle +
    recordingMortgage +
    recordingDeedBase +
    exitFee;

  const totalLenderCosts = totalOriginationFees + totalOtherFees;

  // Total interest
  const totalInterest = monthlyPayment * loanTerm;

  // Total cost
  const totalCost = totalInterest + totalLenderCosts;

  // Cash to close (refinance: just closing costs, no down payment)
  const cashToClose = totalLenderCosts;

  // Monthly PITI
  const piti =
    monthlyPayment +
    monthlyPropertyTaxes +
    monthlyHomeownersInsurance +
    hoaFees;

  return {
    type: USER_LOAN_PROGRAM.BRIDGE,
    purchasePrice: currentPropertyValue,
    marketInterestRate: interestRate,
    creditScore,
    loanAmount,
    interestRate,
    monthlyPayment,
    monthlyPrincipalInterest: monthlyPayment,
    loanTerm,
    balloonPayment,
    numberOfPayments: loanTerm,
    totalInterest,
    totalCost,
    cashToClose,
    cashReceived: cashOutAmount,
    piti,
    // Origination fees
    underwritingFee,
    applicationFeeBase,
    processingFeeBase,
    creditReportFeeBase,
    bridgeFee,
    // Other fees
    appraisalFeeBase,
    titleSettlementAgentFee,
    floodDeterminationFee,
    floodMonitoringFee,
    pestInspectionFee,
    surveyFee,
    countyRecordingFees,
    stateRecordingFees,
    transferTaxes,
    prepaidInterest,
    prepaidsHomeownersInsurance,
    initialEscrowTaxes,
    optionalOwnersTitleInsurance,
    insuranceBinder,
    titleTitleSearchBase,
    titleLendersTitleInsuranceBase,
    recordingTitle,
    recordingMortgage,
    recordingDeedBase,
    exitFee,
    // Totals
    totalOriginationFees,
    totalOtherFees,
    totalLenderCosts,
    // Additional fields
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    lenderCredits,
    prepaidsPropertyTaxes,
    initialEscrowMortgageInsurance: 0,
    // Refinance specific
    existingLoanBalance,
    currentPropertyValue,
    ltv,
    loanProgram: USER_LOAN_PROGRAM.BRIDGE,
  };
};
